import React, {
  forwardRef,
  type RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Pressable, View } from 'react-native';

import { webOnlyStyles } from '../../utils';
import { focusOnElement } from '../../utils/accessibility/focus/focusOnElement.web';
import { CollapseIndicatorIcon } from '../Collapsible';
import { FadeView } from '../FadeView';
import { LoadingPlaceholderFitToView } from '../LoadingPlaceholder';
import { BodyText } from '../Text';
import { styles } from './Select.styles';
import type { TriggerProps, TriggerSelectedOptionProps } from './Select.types';

export const Trigger = forwardRef<View, TriggerProps>((props, ref) => {
  const {
    selectedOption,
    opened,
    accessibilityLabel,
    selectId,
    variation,
    isInteracting,
    isLoading,
    onLayout,
    onPress,
  } = props;

  const [isFirstPress, setFirstPress] = useState(true);

  const style = [
    styles.row,
    styles.trigger,
    variation === 'box' ? styles.triggerBox : undefined,
  ];

  const handleTrigger = useCallback(() => {
    setFirstPress(false);
    onPress();
  }, [setFirstPress, onPress]);

  useEffect(() => {
    if (opened || isFirstPress) return;
    focusOnElement(ref as RefObject<View>);
  }, [ref, isFirstPress, opened]);

  return (
    <View style={styles.triggerWrapper}>
      <FadeView show={isInteracting} style={styles.triggerHover} />

      {isLoading ? <LoadingPlaceholderFitToView palette="cream" /> : null}

      <Pressable
        ref={ref}
        style={style}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        onLayout={onLayout}
        onPress={handleTrigger}
        {...{
          accessibilityExpanded: opened,
          accessibilityControls: selectId,
        }}
      >
        <TriggerSelectedOption
          selectedOption={selectedOption}
          bold={variation !== 'box'}
        />

        <CollapseIndicatorIcon expanded={opened} />
      </Pressable>
    </View>
  );
});

// ─── Sub Components ──────────────────────────────────────────────────────────

const TriggerSelectedOption = (props: TriggerSelectedOptionProps) => {
  const { bold, selectedOption = '' } = props;

  return (
    <BodyText
      size={4}
      bold={bold}
      style={webOnlyStyles({ userSelect: 'none' })}
    >
      {selectedOption}
    </BodyText>
  );
};
